import type { Params } from 'react-router-dom';

import { decide } from '../domain/decide';
import type { Decision } from '../domain/decide';
import { anyPlant, atPlant } from '../domain/rules';
import type { AccessRule, Scope } from '../domain/rules';
import type { MeAccess } from '../types';

export type PlantSource =
  | { readonly from: 'none' }
  | { readonly from: 'param'; readonly name: string }
  | { readonly from: 'search'; readonly name: string };

export interface RouteAccess {
  readonly rule: AccessRule;
  readonly plant: PlantSource;
}

export const none: PlantSource = { from: 'none' };

export const param = (name: string): PlantSource => ({ from: 'param', name });

export const search = (name: string): PlantSource => ({ from: 'search', name });

export const access = (rule: AccessRule, plant: PlantSource = none): { readonly access: RouteAccess } => ({ access: { rule, plant } });

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isRule = (value: unknown): value is AccessRule => isObject(value) && typeof value.kind === 'string';

const isPlantSource = (value: unknown): value is PlantSource =>
  isObject(value) && (value.from === 'none' || ((value.from === 'param' || value.from === 'search') && typeof value.name === 'string'));

export const routeAccessOf = (handle: unknown): RouteAccess | undefined => {
  if (!isObject(handle) || !isObject(handle.access)) return undefined;
  const { rule, plant } = handle.access;
  return isRule(rule) && isPlantSource(plant) ? { rule, plant } : undefined;
};

export const scopeOf = (plant: PlantSource, params: Params, searchParams: URLSearchParams): Scope => {
  switch (plant.from) {
    case 'none':
      return anyPlant;
    case 'param':
      return atPlant(params[plant.name]);
    case 'search':
      return atPlant(searchParams.get(plant.name));
    default: {
      const unhandled: never = plant;
      throw new Error(`Plant source without a scope: ${JSON.stringify(unhandled)}`);
    }
  }
};

export interface BranchMatch {
  readonly handle: unknown;
  readonly params: Params;
}

export interface BranchDecision {
  readonly decision: Decision;
  readonly failed?: { readonly rule: AccessRule; readonly scope: Scope };
}

const UNDECLARED: BranchDecision = { decision: { allow: false, reason: 'UNDECLARED' } };

export const decideBranch = (me: MeAccess, branch: readonly BranchMatch[], searchParams: URLSearchParams): BranchDecision => {
  const declared = branch.flatMap((match) => {
    const routeAccess = routeAccessOf(match.handle);
    return routeAccess === undefined ? [] : [{ ...routeAccess, scope: scopeOf(routeAccess.plant, match.params, searchParams) }];
  });
  if (declared.length === 0) return UNDECLARED;
  const decided = declared.map((entry) => ({ entry, decision: decide(me, entry.rule, entry.scope) }));
  const refusal = decided.find(({ decision }) => !decision.allow);
  if (refusal) return { decision: refusal.decision, failed: { rule: refusal.entry.rule, scope: refusal.entry.scope } };
  return { decision: decided[decided.length - 1]?.decision ?? UNDECLARED.decision };
};
